import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  QueryConstraint,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  Customer,
  CustomerInput,
  CustomerSearchResult,
  CustomerWithStats,
  CustomerFilter,
} from '../types/Customer';
import { PaymentLink } from '../types/PaymentLink';
import { ExcelCustomerRow, validateCustomerImport } from '../utils/excelUtils';

const CUSTOMERS_COLLECTION = 'customers';
const PAYMENT_LINKS_COLLECTION = 'paymentLinks';

/**
 * Search customers by name or email (for autocomplete)
 * Returns max 10 results
 */
export const searchCustomers = async (searchTerm: string): Promise<CustomerSearchResult[]> => {
  if (!searchTerm || searchTerm.trim().length < 2) {
    return [];
  }

  const searchLower = searchTerm.toLowerCase().trim();
  const customersRef = collection(db, CUSTOMERS_COLLECTION);

  try {
    // Search by email (starts with)
    const emailQuery = query(
      customersRef,
      where('deleted', '!=', true),
      where('email', '>=', searchLower),
      where('email', '<=', searchLower + '\uf8ff'),
      orderBy('email'),
      limit(10)
    );

    const emailSnapshot = await getDocs(emailQuery);
    const emailResults = emailSnapshot.docs.map((doc) => {
      const data = doc.data() as Customer;
      return {
        id: doc.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        lastPaymentDate: data.lastPaymentDate,
        totalPayments: data.totalPayments || 0,
      };
    });

    // Search by name (contains - client-side filter with more results)
    const nameQuery = query(
      customersRef,
      where('deleted', '!=', true),
      orderBy('name'),
      limit(200) // Increased limit for better search coverage
    );

    const nameSnapshot = await getDocs(nameQuery);
    const nameResults = nameSnapshot.docs
      .filter((doc) => {
        const data = doc.data() as Customer;
        const nameLower = data.name.toLowerCase();
        // Match if name contains search term OR starts with it
        return nameLower.includes(searchLower) || 
               nameLower.split(' ').some(word => word.startsWith(searchLower));
      })
      .slice(0, 15) // Slightly more results
      .map((doc) => {
        const data = doc.data() as Customer;
        return {
          id: doc.id,
          name: data.name,
          email: data.email,
          phone: data.phone,
          lastPaymentDate: data.lastPaymentDate,
          totalPayments: data.totalPayments || 0,
        };
      });

    // Merge and deduplicate results
    const allResults = [...emailResults, ...nameResults];
    const uniqueResults = Array.from(
      new Map(allResults.map((item) => [item.id, item])).values()
    );

    // Sort by relevance (exact match first, then by name)
    return uniqueResults
      .sort((a, b) => {
        const aExactEmail = a.email === searchLower;
        const bExactEmail = b.email === searchLower;
        if (aExactEmail && !bExactEmail) return -1;
        if (!aExactEmail && bExactEmail) return 1;
        return a.name.localeCompare(b.name);
      })
      .slice(0, 10);
  } catch (error) {
    console.error('Error searching customers:', error);
    throw error;
  }
};

/**
 * Get customer by ID
 */
export const getCustomerById = async (customerId: string): Promise<Customer | null> => {
  try {
    const customerRef = doc(db, CUSTOMERS_COLLECTION, customerId);
    const customerSnap = await getDoc(customerRef);

    if (!customerSnap.exists()) {
      return null;
    }

    return {
      id: customerSnap.id,
      ...customerSnap.data(),
    } as Customer;
  } catch (error) {
    console.error('Error getting customer:', error);
    throw error;
  }
};

/**
 * Get customer by email
 */
export const getCustomerByEmail = async (email: string): Promise<Customer | null> => {
  try {
    const emailLower = email.toLowerCase().trim();
    const customersRef = collection(db, CUSTOMERS_COLLECTION);
    const q = query(customersRef, where('email', '==', emailLower), limit(1));

    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data(),
    } as Customer;
  } catch (error) {
    console.error('Error getting customer by email:', error);
    throw error;
  }
};

/**
 * Create a new customer
 * Returns the customer ID
 */
export const createCustomer = async (
  input: CustomerInput,
  createdBy?: string
): Promise<string> => {
  try {
    const emailLower = input.email.toLowerCase().trim();

    // Check if customer already exists
    const existing = await getCustomerByEmail(emailLower);
    if (existing) {
      throw new Error('Customer with this email already exists');
    }

    const customersRef = collection(db, CUSTOMERS_COLLECTION);
    const newCustomerRef = doc(customersRef);

    const customer: any = {
      email: emailLower,
      name: input.name.trim(),
      totalPayments: 0,
      totalAmount: 0,
      deleted: false,
      createdAt: Timestamp.now(),
    };

    // Only add optional fields if they exist
    if (input.phone?.trim()) {
      customer.phone = input.phone.trim();
    }
    if (createdBy) {
      customer.createdBy = createdBy;
    }
    if (input.notes?.trim()) {
      customer.notes = input.notes.trim();
    }

    await setDoc(newCustomerRef, customer);

    return newCustomerRef.id;
  } catch (error) {
    console.error('Error creating customer:', error);
    throw error;
  }
};

/**
 * Update customer information
 */
export const updateCustomer = async (
  customerId: string,
  input: Partial<CustomerInput>,
  updatedBy?: string
): Promise<void> => {
  try {
    const customerRef = doc(db, CUSTOMERS_COLLECTION, customerId);

    const updateData: Partial<Customer> = {
      updatedAt: Timestamp.now() as unknown as Timestamp,
      updatedBy,
    };

    if (input.name) updateData.name = input.name.trim();
    if (input.phone !== undefined) updateData.phone = input.phone?.trim();
    if (input.notes !== undefined) updateData.notes = input.notes?.trim();
    if (input.email) updateData.email = input.email.toLowerCase().trim();

    await updateDoc(customerRef, updateData);
  } catch (error) {
    console.error('Error updating customer:', error);
    throw error;
  }
};

/**
 * Soft delete customer
 */
export const softDeleteCustomer = async (
  customerId: string,
  deletedBy?: string
): Promise<void> => {
  try {
    const customerRef = doc(db, CUSTOMERS_COLLECTION, customerId);

    await updateDoc(customerRef, {
      deleted: true,
      deletedAt: Timestamp.now(),
      deletedBy,
    });
  } catch (error) {
    console.error('Error deleting customer:', error);
    throw error;
  }
};

/**
 * Restore soft-deleted customer
 */
export const restoreCustomer = async (customerId: string): Promise<void> => {
  try {
    const customerRef = doc(db, CUSTOMERS_COLLECTION, customerId);

    await updateDoc(customerRef, {
      deleted: false,
      deletedAt: null,
      deletedBy: null,
    });
  } catch (error) {
    console.error('Error restoring customer:', error);
    throw error;
  }
};

/**
 * Get all payment links for a customer
 */
export const getCustomerPaymentLinks = async (customerId: string): Promise<PaymentLink[]> => {
  try {
    const linksRef = collection(db, PAYMENT_LINKS_COLLECTION);
    const q = query(
      linksRef,
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as PaymentLink[];
  } catch (error) {
    console.error('Error getting customer payment links:', error);
    throw error;
  }
};

/**
 * Get all customers with stats
 */
export const getAllCustomers = async (
  filter?: CustomerFilter
): Promise<CustomerWithStats[]> => {
  try {
    const customersRef = collection(db, CUSTOMERS_COLLECTION);
    const constraints: QueryConstraint[] = [];

    // Filter deleted
    if (filter?.deleted === true) {
      // Only show deleted customers
      constraints.push(where('deleted', '==', true));
    }
    // If deleted is false or undefined, show non-deleted (including those without deleted field)

    // Sort
    const sortField = filter?.sortBy || 'name';
    const sortDirection = filter?.sortOrder || 'asc';
    constraints.push(orderBy(sortField, sortDirection));

    const q = query(customersRef, ...constraints);
    const snapshot = await getDocs(q);

    // Quick map without individual queries (much faster)
    const customers = snapshot.docs.map((doc) => {
      const data = doc.data() as Omit<Customer, 'id'>;

      return {
        id: doc.id,
        ...data,
        paymentLinksCount: 0, // Set to 0 for now (can be calculated on-demand if needed)
        pendingLinksCount: 0,
      } as CustomerWithStats;
    });

    // Client-side search filter
    if (filter?.search) {
      const searchLower = filter.search.toLowerCase();
      return customers.filter(
        (c) =>
          c.name.toLowerCase().includes(searchLower) ||
          c.email.toLowerCase().includes(searchLower) ||
          c.phone?.toLowerCase().includes(searchLower)
      );
    }

    return customers;
  } catch (error) {
    console.error('Error getting all customers:', error);
    throw error;
  }
};

/**
 * Get or create customer by email
 * Used when creating payment links
 * Validates that email and name are not empty to prevent unnamed customers
 */
export const getOrCreateCustomer = async (
  email: string,
  name: string,
  phone?: string,
  createdBy?: string
): Promise<string> => {
  try {
    const emailLower = email.toLowerCase().trim();
    const nameTrimmed = name.trim();

    // Validate required fields - prevent empty customers
    if (!emailLower || emailLower.length < 3 || !emailLower.includes('@')) {
      throw new Error('Valid email is required to create/update customer');
    }

    if (!nameTrimmed || nameTrimmed.length < 2) {
      throw new Error('Valid name is required to create/update customer (minimum 2 characters)');
    }

    // Try to find existing customer
    const existing = await getCustomerByEmail(emailLower);

    if (existing) {
      // Update customer info if changed
      const updates: Partial<CustomerInput> = {};
      let needsUpdate = false;

      if (existing.name !== nameTrimmed) {
        updates.name = nameTrimmed;
        needsUpdate = true;
      }

      if (phone && existing.phone !== phone.trim()) {
        updates.phone = phone.trim();
        needsUpdate = true;
      }

      if (needsUpdate) {
        await updateCustomer(existing.id, updates, createdBy);
      }

      return existing.id;
    }

    // Create new customer
    return await createCustomer(
      {
        email: emailLower,
        name: nameTrimmed,
        phone: phone?.trim() || undefined,
        notes: undefined,
      },
      createdBy
    );
  } catch (error) {
    console.error('Error in getOrCreateCustomer:', error);
    throw error;
  }
};

/**
 * Import customers from Excel data
 * Validates and imports only new customers (no duplicates)
 */
export const importCustomersFromExcel = async (
  excelCustomers: ExcelCustomerRow[],
  importedBy: string
): Promise<{
  success: number;
  skipped: number;
  errors: string[];
}> => {
  try {
    // Get all existing customers
    const existingCustomers = await getAllCustomers({ deleted: false });

    // Validate import data
    const validation = validateCustomerImport(excelCustomers, existingCustomers);

    if (validation.valid.length === 0) {
      return {
        success: 0,
        skipped: validation.duplicates.length + validation.invalid.length,
        errors: [
          ...validation.duplicates.map(d => `${d.name} (${d.email}): ${d.reason}`),
          ...validation.invalid.map(i => `${i.name} (${i.email}): ${i.reason}`),
        ],
      };
    }

    // Batch import valid customers
    const batch = writeBatch(db);
    const customersRef = collection(db, CUSTOMERS_COLLECTION);
    let successCount = 0;

    for (const customer of validation.valid) {
      const customerId = doc(customersRef).id; // Generate new ID
      
      // Build customer data without undefined fields (Firestore doesn't accept undefined)
      const customerData: any = {
        email: customer.email.toLowerCase().trim(),
        name: customer.name.trim(),
        totalPayments: 0,
        totalAmount: 0,
        createdAt: Timestamp.now(),
        createdBy: importedBy,
        deleted: false,
        migrated: true, // Mark as imported from Excel
      };

      // Only add optional fields if they have values
      if (customer.phone?.trim()) {
        customerData.phone = customer.phone.trim();
      }
      if (customer.notes?.trim()) {
        customerData.notes = customer.notes.trim();
      }

      batch.set(doc(customersRef, customerId), customerData);
      successCount++;
    }

    // Commit batch
    await batch.commit();

    return {
      success: successCount,
      skipped: validation.duplicates.length + validation.invalid.length,
      errors: [
        ...validation.duplicates.map(d => `Skipped: ${d.name} (${d.email}) - ${d.reason}`),
        ...validation.invalid.map(i => `Invalid: ${i.name} (${i.email}) - ${i.reason}`),
      ],
    };
  } catch (error) {
    console.error('Error importing customers:', error);
    throw error;
  }
};

